import React, { useState } from 'react';
import {
    Globe, Plus, Shield, Check, X, Clock, AlertTriangle,
    MoreVertical, RefreshCw, Trash2, Eye, Mail, Database, Terminal
} from 'lucide-react';
import { Input } from '@/components/common/ui/Input';
import { Button } from '@/components/common/ui/Button';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator
} from '@/components/common/ui/DropdownMenu';
import ConfirmationModal from '@/components/common/ui/ConfirmationModal';
import {
    useGetDomainVerificationsQuery,
    useInitiateDomainVerificationMutation,
    useDeleteDomainVerificationMutation,
    useLazyGetDomainVerificationInstructionsQuery
} from '@/lib/services/domainVerificationApi';
import { useToast } from '@/hooks/components/common/toast/useToast';
import DomainVerificationInstructionsModal from './DomainVerificationInstructionsModal';
import type {
    DomainVerificationItem,
    DomainVerificationMethod,
    DomainVerificationStatus,
    DomainVerificationInstructions
} from '@/lib/api/types';

// Cyber Status Badge
const StatusBadge: React.FC<{ status: DomainVerificationStatus }> = ({ status }) => {
    const configs = {
        verified: { border: 'border-[#50A970]/40 text-[#50A970] bg-[#50A970]/5', icon: <Check className="h-3 w-3" /> },
        pending: { border: 'border-[#EAB308]/40 text-[#EAB308] bg-[#EAB308]/5', icon: <Clock className="h-3 w-3" /> },
        failed: { border: 'border-[#FF3333]/40 text-[#FF3333] bg-[#FF3333]/5', icon: <X className="h-3 w-3" /> },
        expired: { border: 'border-slate-600 text-slate-500 bg-slate-900/20', icon: <AlertTriangle className="h-3 w-3" /> }
    };

    const config = configs[status];
    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[9px] font-bold uppercase tracking-widest ${config.border}`}>
            {config.icon}
            {status}
        </span>
    );
};

// Cyber Method Badge
const MethodBadge: React.FC<{ method: DomainVerificationMethod }> = ({ method }) => {
    const config = method === 'dns'
        ? { icon: <Database className="h-3 w-3" />, label: 'DNS_RECORD' }
        : { icon: <Mail className="h-3 w-3" />, label: 'ADMIN_MAIL' };

    return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-[#00FFFF]/30 bg-[#00FFFF]/5 text-[#00FFFF] text-[9px] font-bold uppercase tracking-widest">
            {config.icon}
            {config.label}
        </span>
    );
};

const DomainVerification: React.FC = () => {
    const [newDomain, setNewDomain] = useState('');
    const [verificationMethod, setVerificationMethod] = useState<DomainVerificationMethod>('dns');
    const [adminEmail, setAdminEmail] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [selectedDomain, setSelectedDomain] = useState<DomainVerificationItem | null>(null);
    const [domainInstructions, setDomainInstructions] = useState<DomainVerificationInstructions | null>(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [domainToDelete, setDomainToDelete] = useState<{ id: number; domain: string } | null>(null);

    const toast = useToast();

    const {
        data: verificationsResponse,
        isLoading: verificationsLoading,
    } = useGetDomainVerificationsQuery();

    const [initiateDomainVerification, { isLoading: initiateLoading }] = useInitiateDomainVerificationMutation();
    const [deleteDomainVerification, { isLoading: deleteLoading }] = useDeleteDomainVerificationMutation();
    const [getInstructions] = useLazyGetDomainVerificationInstructionsQuery();

    const verifications = verificationsResponse?.verifications || [];
    const planLimits = verificationsResponse?.plan_limits;

    const handleAddDomain = async () => {
        if (!newDomain.trim()) {
            toast({ type: 'error', title: 'INPUT_REQUIRED', message: 'Domain field cannot be empty.' });
            return;
        }

        try {
            const result = await initiateDomainVerification({
                domain: newDomain.trim(),
                method: verificationMethod,
                admin_email: verificationMethod === 'email' ? adminEmail.trim() : undefined
            }).unwrap();

            toast({ type: 'success', title: 'PROTOCOL_INITIATED', message: `Verification sequence started for ${result.domain}` });
            setNewDomain(''); setAdminEmail(''); setShowAddForm(false);
        } catch (error: any) {
            toast({ type: 'error', title: 'INIT_FAILURE', message: error?.data?.message || 'Handshake failed.' });
        }
    };

    const handleDeleteClick = (verificationId: number, domain: string) => {
        setDomainToDelete({ id: verificationId, domain });
        setShowDeleteConfirmation(true);
    };

    const handleConfirmDelete = async () => {
        if (!domainToDelete) return;
        try {
            await deleteDomainVerification(domainToDelete.id).unwrap();
            toast({ type: 'success', title: 'NODE_PURGED', message: `Trust anchor removed for ${domainToDelete.domain}` });
            setShowDeleteConfirmation(false);
            setDomainToDelete(null);
        } catch (error: any) {
            toast({ type: 'error', title: 'DELETE_FAILURE', message: 'Failed to purge node.' });
        }
    };

    const handleViewInstructions = async (domain: DomainVerificationItem) => {
        try {
            const result = await getInstructions(domain.id).unwrap();
            setSelectedDomain(domain);
            setDomainInstructions(result.instructions);
        } catch (error: any) {
            toast({ type: 'error', title: 'DATA_UNAVAILABLE', message: 'Could not fetch instructions.' });
        }
    };

    const canAddMoreDomains = planLimits ? (planLimits.unlimited_domains || planLimits.can_add_more) : true;
    const remainingDomains = planLimits ? (planLimits.unlimited_domains ? 'UNLIMITED' : planLimits.max_domains - planLimits.verified) : 0;

    if (verificationsLoading) {
        return (
            <div className="p-12 flex flex-col items-center justify-center font-mono gap-4">
                <RefreshCw className="h-8 w-8 animate-spin text-[#00FFFF]" />
                <span className="text-[10px] text-slate-500 uppercase tracking-[0.3em]">Querying_Trust_Anchors...</span>
            </div>
        );
    }

    return (
        <div className="font-mono">
            <div className="space-y-6">
                {/* Header Section */}
                <div className="flex items-center justify-between pb-6 border-b border-[#1A1D24]">
                    <div className="flex items-center gap-3">
                        <Terminal className="h-5 w-5 text-[#00FFFF]" />
                        <h3 className="text-sm font-bold text-white uppercase tracking-widest">
                            Domain Verification
                        </h3>
                    </div>

                    <Button
                        onClick={() => setShowAddForm(true)}
                        disabled={!canAddMoreDomains || initiateLoading}
                        className="bg-[#00FFFF] text-black hover:bg-[#00FFFF]/80 font-black h-9 px-4 text-[10px] tracking-widest shadow-[0_0_15px_rgba(0,255,255,0.2)]"
                        leftIcon={<Plus className="h-3.5 w-3.5" />}
                    >
                        NEW_DOMAIN
                    </Button>
                </div>

                {/* Cyber Usage Stats */}
                {planLimits && (
                    <div className="bg-[#050505] border border-[#1A1D24] rounded-xl p-5 shadow-inner">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Domain Usage</span>
                            <span className="text-[11px] font-bold text-white tracking-tighter">
                                {planLimits.verified} / {planLimits.unlimited_domains ? 'INF' : planLimits.max_domains}
                            </span>
                        </div>
                        <div className="w-full bg-[#1A1D24] rounded-full h-1.5 overflow-hidden">
                            <div
                                className="bg-[#00FFFF] h-full shadow-[0_0_8px_#00FFFF] transition-all duration-1000"
                                style={{ width: `${(planLimits.verified / (planLimits.max_domains || 1)) * 100}%` }}
                            />
                        </div>
                        <div className="mt-3 text-[9px] text-slate-600 uppercase tracking-widest">
                            [SYS_LOG] {planLimits.unlimited_domains ? 'Unlimited scaling enabled' : `${remainingDomains} additional slots available`}
                        </div>
                    </div>
                )}

                {/* Add Domain Form */}
                {showAddForm && (
                    <div className="bg-[#0A0B0E] border border-[#00FFFF]/30 rounded-xl p-6 shadow-[0_0_30px_rgba(0,255,255,0.05)] animate-in fade-in slide-in-from-top-2">
                        <h4 className="text-[11px] font-bold text-[#00FFFF] uppercase tracking-widest mb-6 flex items-center gap-2">
                            <Plus className="w-3 h-3" /> Add New Domain
                        </h4>

                        <div className="space-y-6">
                            <Input
                                label="Domain"
                                placeholder="example.com"
                                value={newDomain}
                                onChange={(e) => setNewDomain(e.target.value)}
                                className="bg-black border-[#1A1D24] focus:border-[#00FFFF] text-[#00FFFF] h-11 text-[12px] uppercase tracking-wider"
                            />

                            <div className="space-y-3">
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                    VERIFICATION_METHOD
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {[
                                        { id: 'dns', icon: Database, label: 'DNS_RECORD', sub: 'Add a TXT record to your DNS settings' },
                                        { id: 'email', icon: Mail, label: 'ADMIN_MAIL', sub: 'Verify via email sent to domain admin' }
                                    ].map((m) => (
                                        <button
                                            key={m.id}
                                            onClick={() => setVerificationMethod(m.id as DomainVerificationMethod)}
                                            className={`p-4 rounded-xl border text-left transition-all group ${verificationMethod === m.id
                                                ? 'border-[#00FFFF] bg-[#00FFFF]/5 shadow-[inset_0_0_10px_rgba(0,255,255,0.05)]'
                                                : 'border-[#1A1D24] bg-black hover:border-slate-600'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3 mb-1">
                                                <m.icon className={`h-4 w-4 ${verificationMethod === m.id ? 'text-[#00FFFF]' : 'text-slate-500'}`} />
                                                <span className={`text-[11px] font-bold tracking-widest ${verificationMethod === m.id ? 'text-white' : 'text-slate-400'}`}>
                                                    {m.label}
                                                </span>
                                            </div>
                                            <p className="text-[9px] text-slate-600 uppercase tracking-tight leading-relaxed">{m.sub}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {verificationMethod === 'email' && (
                                <Input
                                    label="ADMIN_CONTACT_EMAIL"
                                    type="email"
                                    value={adminEmail}
                                    onChange={(e) => setAdminEmail(e.target.value)}
                                    placeholder="admin@target.com"
                                    className="bg-black border-[#1A1D24] focus:border-[#00FFFF] text-white h-11 text-[12px]"
                                />
                            )}

                            <div className="flex items-center gap-3 pt-2">
                                <Button
                                    onClick={handleAddDomain}
                                    loading={initiateLoading}
                                    disabled={!newDomain.trim() || (verificationMethod === 'email' && !adminEmail.trim())}
                                    className="bg-[#00FFFF] text-black font-black px-6 h-10 text-[10px] tracking-widest"
                                >
                                    Start Verification
                                </Button>
                                <button
                                    onClick={() => setShowAddForm(false)}
                                    className="px-4 py-2 text-[10px] font-bold text-slate-500 hover:text-white uppercase tracking-widest transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Domains List */}
                <div className="grid grid-cols-1 gap-4">
                    {verifications.length === 0 ? (
                        <div className="text-center py-16 border border-dashed border-[#1A1D24] rounded-2xl">
                            <Globe className="h-10 w-10 text-slate-800 mx-auto mb-4 opacity-30" />
                            <h3 className="text-sm font-bold text-slate-600 uppercase tracking-[0.3em]">No Domains Verified</h3>
                        </div>
                    ) : (
                        verifications.map((v) => (
                            <div key={v.id} className="bg-[#050505] border border-[#1A1D24] rounded-xl p-5 hover:bg-[#0A0B0E] transition-colors group">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 bg-[#12151C] border border-[#1A1D24] rounded-lg flex items-center justify-center group-hover:border-[#00FFFF]/30 transition-colors shadow-inner">
                                            <Globe className="h-5 w-5 text-slate-500 group-hover:text-[#00FFFF] transition-colors" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[13px] font-bold text-white tracking-tight">{v.domain}</span>
                                                {v.is_verified && <Shield className="h-3.5 w-3.5 text-[#50A970] drop-shadow-[0_0_5px_rgba(80,169,112,0.5)]" />}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <StatusBadge status={v.status} />
                                                <MethodBadge method={v.method} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger className="p-2 text-slate-500 hover:text-white hover:bg-[#1A1D24] rounded-lg transition-colors outline-none border border-transparent hover:border-[#1A1D24]">
                                                <MoreVertical className="h-4 w-4" />
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="bg-[#0A0B0E] border-[#1A1D24] shadow-2xl font-mono">
                                                <DropdownMenuItem onClick={() => handleViewInstructions(v)} className="focus:bg-[#12151C] focus:text-[#00FFFF] text-[11px] uppercase tracking-wider cursor-pointer">
                                                    <Eye className="h-3.5 w-3.5 mr-2" /> View_Instructions
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator className="bg-[#1A1D24]" />
                                                <DropdownMenuItem
                                                    onClick={() => handleDeleteClick(v.id, v.domain)}
                                                    className="focus:bg-[#FF3333]/10 text-[#FF3333] text-[11px] uppercase tracking-wider cursor-pointer"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Purge_Registry
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </div>

                                {/* Metadata Grid */}
                                <div className="mt-5 pt-4 border-t border-[#1A1D24] grid grid-cols-2 sm:grid-cols-3 gap-6">
                                    <div>
                                        <p className="text-[9px] text-slate-600 uppercase tracking-widest mb-1">Created_Date</p>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase">{new Date(v.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] text-slate-600 uppercase tracking-widest mb-1">TTL_Expiration</p>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase">{new Date(v.expires_at).toLocaleDateString()}</p>
                                    </div>
                                    {v.verified_at && (
                                        <div className="col-span-2 sm:col-span-1">
                                            <p className="text-[9px] text-[#50A970] uppercase tracking-widest mb-1">Trust_Established</p>
                                            <p className="text-[10px] text-white font-bold uppercase">{new Date(v.verified_at).toLocaleDateString()}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Instructions Modal Integration */}
            {selectedDomain && domainInstructions && (
                <DomainVerificationInstructionsModal
                    domain={selectedDomain}
                    instructions={domainInstructions}
                    onClose={() => {
                        setSelectedDomain(null);
                        setDomainInstructions(null);
                    }}
                />
            )}

            {/* Delete Confirmation Modal Integration */}
            <ConfirmationModal
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={handleConfirmDelete}
                title="PURGE_DOMAIN_TRUST"
                description={`Warning: Removing the trust anchor for "${domainToDelete?.domain}" will immediately suspend intelligence exfiltration capabilities for this node.`}
                confirmText="PURGE"
                cancelText="ABORT"
                variant="danger"
                loading={deleteLoading}
                icon={<Trash2 className="h-6 w-6" />}
            />
        </div>
    );
};

export default DomainVerification;